import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useMessengerContext } from "./useMessengerContext";
import { useAuthContext } from "./useAuthContext";

const useSocket = (x, conversationId) => {
  const socket = useRef();
  const [unOpenedMessage, setUnOpenedMessage] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const { dispatch } = useMessengerContext();
  const { user } = useAuthContext();
  const userId = user ? user.userId : null;
  const isCurrentlyTyping = useRef(false);
  
  const backendUrl = process.env.REACT_APP_ENV_ENVIRONMENT === "development"
    ? "ws://localhost:4000"
    : "wss://interconnector-ae747.ew.r.appspot.com";


  // Single effect: create socket, attach all listeners, join room.
  // Keeping listeners here (not a separate effect) ensures they are always
  // re-attached whenever the socket is recreated due to conversationId changing.
  useEffect(() => {
    socket.current = io(backendUrl, {
      transports: ["polling", "websocket"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 10000,
    });

    const handleConnect = () => {
      console.log("🟢 Socket connected, ID:", socket.current.id);
      setIsSocketConnected(true);
      if (userId && conversationId) {
        console.log("👋 Adding user to conversation:", { userId, conversationId });
        socket.current.emit("addUser", userId, conversationId);
        socket.current.emit("updateUserConversation", { userId, conversationId });
      } else {
        console.log("❌ Missing userId or conversationId:", { userId, conversationId });
      }
    };

    const handleDisconnect = (reason) => {
      console.warn("🔴 Socket disconnected:", reason);
      setIsSocketConnected(false);
    };

    const handleConnectError = (err) => {
      console.error("❌ [Socket] Connection error:", err);
    };

    const handleReconnect = (attemptNumber) => {
      console.log(`🔄 [Socket] Reconnected after ${attemptNumber} attempts`);
    };

    const handleMessage = (data) => {
      console.log("[messenger load debug] 📨 getMessage received:", {
        sender: data.sender,
        text: data.text?.substring(0, 50),
        messageId: data.messageId,
        suggestions: data.suggestions?.length || 0,
        source: data.source
      });
      if (data.suggestions?.length > 0) {
        console.log("[suggestions automatic] 🎉 FRONTEND RECEIVED SUGGESTION MESSAGE!", data.suggestions);
      }
      dispatch({
        type: "ADD_MESSAGE",
        payload: {
          sender: data.sender,
          text: data.text,
          _id: data._id,
          createdAt: data.createdAt || new Date().toISOString(),
          messageId: data.messageId,
          suggestions: data.suggestions || [],
          quickReplies: data.quickReplies || [],
          communityId: data.communityId,
          conversationId: data.conversationId,
        },
      });
    };

    const handleUpdateAlert = (data) => {
      dispatch({ type: "UPDATE_CONVERSATION_UNREAD", payload: data.conversationId });
    };

    const handleNewConversation = (conversation) => {
      dispatch({ type: "ADD_CONVERSATION", payload: conversation });
    };

    const handleLastReadUpdate = ({ messageId, userId: uid }) => {
      dispatch({ type: "UPDATE_LAST_READ_MESSAGE_ID", payload: { userId: uid, messageId } });
    };

    const handleUserTyping = ({ userId: uid, conversationId: cid, timestamp }) => {
      dispatch({ type: "SET_USER_TYPING", payload: { userId: uid, conversationId: cid, timestamp } });
    };

    const handleUserStoppedTyping = ({ userId: uid, conversationId: cid, timestamp }) => {
      dispatch({ type: "SET_USER_STOPPED_TYPING", payload: { userId: uid, conversationId: cid, timestamp } });
    };

    const handleBotTyping = ({ botId, conversationId: cid, timestamp }) => {
      dispatch({ type: "SET_BOT_TYPING", payload: { botId, conversationId: cid, timestamp } });
    };

    const handleBotStoppedTyping = ({ botId, conversationId: cid, timestamp }) => {
      dispatch({ type: "SET_BOT_STOPPED_TYPING", payload: { botId, conversationId: cid, timestamp } });
    };

    const handleMemberJoined = ({ conversationId: cid, userId: uid }) => {
      dispatch({ type: 'GROUP_MEMBER_JOINED', payload: { conversationId: cid, userId: uid } });
    };

    const handleGroupBotInvited = ({ conversationId: cid }) => {
      dispatch({ type: 'GROUP_BOT_INVITED', payload: { conversationId: cid } });
    };

    socket.current.on("connect", handleConnect);
    socket.current.on("disconnect", handleDisconnect);
    socket.current.on("connect_error", handleConnectError);
    socket.current.on("reconnect", handleReconnect);
    socket.current.on("getMessage", handleMessage);
    socket.current.on("updateAlert", handleUpdateAlert);
    socket.current.on("receiveConversation", handleNewConversation);
    socket.current.on("lastReadMessageUpdated", handleLastReadUpdate);
    socket.current.on("userTyping", handleUserTyping);
    socket.current.on("userStoppedTyping", handleUserStoppedTyping);
    socket.current.on("botTyping", handleBotTyping);
    socket.current.on("botStoppedTyping", handleBotStoppedTyping);
    socket.current.on("memberJoined", handleMemberJoined);
    socket.current.on("groupBotInvited", handleGroupBotInvited);

    return () => {
      socket.current.disconnect();
    };
  }, [backendUrl, userId, conversationId, dispatch]);

  // Message sending function
  const sendMessage = (message) => {
    if (!socket.current?.connected) {
      console.log("❌ Socket not connected, cannot send message. Connection state:", socket.current?.connected);
      return;
    }
    console.log("🚀 Emitting sendMessage:", message);
    socket.current.emit("sendMessage", message);
  };

  // Typing indicator functions
  const startTyping = () => {
    if (!socket.current?.connected || !userId || !conversationId) {
      console.log("❌ Cannot start typing - missing connection or data");
      return;
    }
    // Always emit typing event (removed the guard)
    console.log("📤 Emitting typingStart:", { userId, conversationId });
    isCurrentlyTyping.current = true;
    socket.current.emit("typingStart", { userId, conversationId });
  };

  const stopTyping = () => {
    console.log("🔍 STOP TYPING CALLED - Stack trace:", new Error().stack);
    if (!socket.current?.connected || !userId || !conversationId) {
      console.log("❌ Cannot stop typing - missing connection or data");
      return;
    }
    if (isCurrentlyTyping.current) {
      console.log("📤 Emitting typingStop:", { userId, conversationId });
      isCurrentlyTyping.current = false;
      socket.current.emit("typingStop", { userId, conversationId });
    } else {
      console.log("⚠️ Attempted to stop typing but wasn't currently typing");
    }
  };

  return { unOpenedMessage, sendMessage, startTyping, stopTyping, isSocketConnected };
};

export default useSocket;


// import { useEffect, useRef, useState } from "react";
// import { io } from "socket.io-client";
// import { useMessengerContext } from "./useMessengerContext";
// import { useAuthContext } from "./useAuthContext";

// const useSocket = (x, conversationId) => {
//   const socket = useRef();
//   const [unOpenedMessage, setUnOpenedMessage] = useState(null);
//   const { dispatch } = useMessengerContext();
//   const { user } = useAuthContext();
//   const userId = user ? user.userId : null;
  
//   const backendUrl = process.env.REACT_APP_ENV_ENVIRONMENT === "development"
//     ? "ws://localhost:4000"
//     : "wss://interconnector-ae747.ew.r.appspot.com";

//   // Socket connection management
//   useEffect(() => {
//     // Initialize socket with WebSocket enforcement
//     socket.current = io(backendUrl, {
//       transports: ["websocket"],
//       withCredentials: true,
//       reconnection: true,
//       reconnectionAttempts: 5,
//       reconnectionDelay: 3000,
//     });

//     // Connection event handlers
//     const handleConnect = () => {
//       console.log("[Socket] Connected, ID:", socket.current.id);
//       if (userId && conversationId) {
//         socket.current.emit("addUser", userId, conversationId);
//       }
//     };

//     const handleDisconnect = (reason) => {
//       console.log("[Socket] Disconnected:", reason);
//     };

//     const handleConnectError = (err) => {
//       console.error("[Socket] Connection error:", err);
//     };

//     socket.current.on("connect", handleConnect);
//     socket.current.on("disconnect", handleDisconnect);
//     socket.current.on("connect_error", handleConnectError);

//     // Cleanup on unmount
//     return () => {
//       console.log("[Socket] Cleaning up connection...");
//       socket.current.off("connect", handleConnect);
//       socket.current.off("disconnect", handleDisconnect);
//       socket.current.off("connect_error", handleConnectError);
//       socket.current.disconnect();
//     };
//   }, [backendUrl, userId, conversationId]);

//   // Message and conversation listeners
//   useEffect(() => {
//     if (!socket.current) return;

//     const handleMessage = (data) => {
//       console.log("[Socket] Message received:", data);
//       dispatch({
//         type: "ADD_MESSAGE",
//         payload: {
//           sender: data.sender,
//           text: data.text,
//           createdAt: Date.now(),
//           messageId: data.messageId,
//         },
//       });
//     };

//     const handleUpdateAlert = (data) => {
//       console.log("[Socket] Update alert:", data);
//       dispatch({
//         type: "UPDATE_CONVERSATION_UNREAD",
//         payload: data.conversationId,
//       });
//     };

//     const handleNewConversation = (conversation) => {
//       console.log("[Socket] New conversation:", conversation);
//       dispatch({ type: "ADD_CONVERSATION", payload: conversation });
//     };

//     const handleLastReadUpdate = ({ messageId, userId }) => {
//       console.log("[Socket] Last read update:", { messageId, userId });
//       dispatch({
//         type: "UPDATE_LAST_READ_MESSAGE_ID",
//         payload: { userId, messageId },
//       });
//     };

//     socket.current.on("getMessage", handleMessage);
//     socket.current.on("updateAlert", handleUpdateAlert);
//     socket.current.on("receiveConversation", handleNewConversation);
//     socket.current.on("lastReadMessageUpdated", handleLastReadUpdate);

//     return () => {
//       socket.current.off("getMessage", handleMessage);
//       socket.current.off("updateAlert", handleUpdateAlert);
//       socket.current.off("receiveConversation", handleNewConversation);
//       socket.current.off("lastReadMessageUpdated", handleLastReadUpdate);
//     };
//   }, [dispatch]);

//   // User/conversation management
//   useEffect(() => {
//     if (!userId || !conversationId || !socket.current) return;

//     console.log("[Socket] Updating user conversation:", { userId, conversationId });
//     socket.current.emit("updateUserConversation", { userId, conversationId });

//     return () => {
//       console.log("[Socket] Cleaning user conversation listeners");
//       socket.current.off("updateUserConversation");
//     };
//   }, [userId, conversationId]);

//   // Message sending function
//   const sendMessage = (message) => {
//     if (!socket.current?.connected) {
//       console.error("[Socket] Cannot send - not connected");
//       return;
//     }
//     console.log("[Socket] Sending message:", message);
//     socket.current.emit("sendMessage", message);
//   };

//   return { unOpenedMessage, sendMessage };
// };

// export default useSocket;


// import { useEffect, useRef, useState } from "react";
// import { io } from "socket.io-client";
// import { useMessengerContext } from "./useMessengerContext";
// import { useAuthContext } from "./useAuthContext";

// const useSocket = (x, conversationId) => {
//   const socket = useRef();
//   const [unOpenedMessage, setUnOpenedMessage] = useState(null);
//   const { dispatch } = useMessengerContext();
//   const { user } = useAuthContext();
//   const userId = user ? user.userId : null;
//   const backendUrl =
//     `${process.env.REACT_APP_ENV_ENVIRONMENT}` === "development"
//       ? "ws://localhost:4000"
//       : "wss://interconnector-ae747.ew.r.appspot.com";

//   console.log("process.env.REACT_APP_ENV_ENVIRONMENT", process.env.REACT_APP_ENV_ENVIRONMENT);

//   useEffect(() => {
//     // Initialize socket connection
//     socket.current = io(backendUrl);

//     socket.current.on("connect", () => {
//       console.log("[Socket] Connected, socket.id:", socket.current.id);
//     });

//     socket.current.on("disconnect", () => {
//       console.log("[Socket] Disconnected");
//     });

//     // Listen for incoming messages
//     socket.current.on("getMessage", (data) => {
//       console.log("[Socket] Message RECEIVED:", data);
//       // Dispatch an action to add the new message to the global state
//       dispatch({
//         type: "ADD_MESSAGE",
//         payload: {
//           sender: data.sender,
//           text: data.text,
//           createdAt: Date.now(),
//           messageId: data.messageId,
//         },
//       });
//     });

//     // Listen for update alerts
//     socket.current.on("updateAlert", (data) => {
//       console.log("[Socket] updateAlert RECEIVED:", data);
//       dispatch({
//         type: "UPDATE_CONVERSATION_UNREAD",
//         payload: data.conversationId,
//       });
//     });

//     // Listen for new conversation
//     socket.current.on("receiveConversation", (conversation) => {
//       console.log("[Socket] receiveConversation RECEIVED:", conversation);
//       dispatch({ type: "ADD_CONVERSATION", payload: conversation });
//     });

//     // Cleanup on unmount
//     return () => {
//       console.log("[Socket] Component unmounting, disconnecting socket...");
//       socket.current.disconnect();
//     };
//   }, []);

//   useEffect(() => {
//     if (!userId || !conversationId) return;

//     // Emit events when the user or current chat changes
//     console.log("[Socket] Emitting updateUserConversation", { userId, conversationId });
//     socket.current.emit("updateUserConversation", {
//       userId: userId,
//       conversationId: conversationId,
//     });
//     console.log("[Socket] Emitting addUser", { userId, conversationId });
//     socket.current.emit("addUser", userId, conversationId);

//     // Cleanup listeners on unmount or when dependencies change
//     return () => {
//       // socket.current.off("getUsers");
//       // socket.current.off("getMessage");
//       // socket.current.off("updateAlert");
//     };
//   }, [userId, conversationId]);

//   // Function to send a message
//   const sendMessage = (message) => {
//     console.log("[Socket] SENDING message:", message, "socket.id:", socket.current.id);
//     socket.current.emit("sendMessage", message);
//   };

//   useEffect(() => {
//     if (!socket.current) return;

//     socket.current.on("lastReadMessageUpdated", ({ messageId, userId }) => {
//       console.log("[Socket] lastReadMessageUpdated RECEIVED:", { messageId, userId });
//       dispatch({
//         type: "UPDATE_LAST_READ_MESSAGE_ID",
//         payload: { userId: userId, messageId: messageId },
//       });
//     });

//     return () => {
//       socket.current.off("lastReadMessageUpdated");
//     };
//   }, []);

//   return { unOpenedMessage, sendMessage };
// };

// export default useSocket;

// // import { useEffect, useRef, useState } from "react";
// // import { io } from "socket.io-client";
// // import { useMessengerContext } from "./useMessengerContext";
// // import { useAuthContext } from "./useAuthContext";

// // const useSocket = (x, conversationId) => {
// //   const socket = useRef();
// //   const [unOpenedMessage, setUnOpenedMessage] = useState(null);
// //   const { dispatch } = useMessengerContext();
// //   const { user } = useAuthContext();
// //   const userId = user ? user.userId : null;
// //   const backendUrl =
// //     `${process.env.REACT_APP_ENV_ENVIRONMENT}` === "development"
// //       ? "ws://localhost:4000"
// //       : "wss://theinterconnector.com/api";

// //   useEffect(() => {
// //     // Initialize socket connection
// //     socket.current = io(backendUrl);

// //     socket.current.on("connect", () => {
// //       console.log("Socket connection state: Connected");
// //     });

// //     socket.current.on("disconnect", () => {
// //       console.log("Socket connection state: Disconnected");
// //     });

// //     // Listen for incoming messages
// //     socket.current.on("getMessage", (data) => {
// //       console.log("getMessage from use Socket", data);
// //       dispatch({
// //         type: "ADD_MESSAGE",
// //         payload: {
// //           sender: data.sender,
// //           text: data.text,
// //           createdAt: Date.now(),
// //           messageId: data.messageId,
// //         },
// //       });
// //     });

// //     // Listen for update alerts
// //     socket.current.on("updateAlert", (data) => {
// //       console.log("updateALERTED");
// //       dispatch({
// //         type: "UPDATE_CONVERSATION_UNREAD",
// //         payload: data.conversationId,
// //       });
// //     });

// //     // Listen for new conversation
// //     socket.current.on("receiveConversation", (conversation) => {
// //       dispatch({ type: "ADD_CONVERSATION", payload: conversation });
// //     });

// //     // Cleanup on unmount
// //     return () => {
// //       console.log("Component is unmounting, disconnecting socket...");
// //       socket.current.disconnect();
// //     };
// //   }, []);

// //   useEffect(() => {
// //     if (!userId || !conversationId) return;

// //     // Emit events when user/conversation changes
// //     socket.current.emit("updateUserConversation", {
// //       userId: userId,
// //       conversationId: conversationId,
// //     });
// //     socket.current.emit("addUser", userId, conversationId);

// //     return () => {
// //       // Cleanup logic if needed
// //     };
// //   }, [userId, conversationId]);

// //   // Function to send a message
// //   const sendMessage = (message) => {
// //     socket.current.emit("sendMessage", message);
// //     console.log("message sent by user, socket ID:", socket.current.id);
// //   };

// //   useEffect(() => {
// //     if (!socket.current) return;

// //     socket.current.on("lastReadMessageUpdated", ({ messageId, userId }) => {
// //       dispatch({
// //         type: "UPDATE_LAST_READ_MESSAGE_ID",
// //         payload: { userId: userId, messageId: messageId },
// //       });
// //       console.log("lastReadMessageUpdated from use Socket", messageId, userId);
// //     });

// //     return () => {
// //       socket.current.off("lastReadMessageUpdated");
// //     };
// //   }, []);

// //   return { unOpenedMessage, sendMessage };
// // };

// // export default useSocket;  // This was the missing export
